import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { requestJson } from "../../../helpers/api";
import baconLogo from "../../../assets/images/baconappen.png";
import {
  exportVentasComparativoExcelBatch,
  exportVentasComparativoExcel,
  exportVentasComparativoPdf,
  type VentasComparativoExportConfig,
} from "./VentasComparativoOffline/exporters";

type TabId = "mes" | "dia" | "semana";
type SemestreId = 1 | 2;

type ComparativoOfflineBaseRow = {
  idsucursal: number | null;
  sucursal: string;
  subdivision: string | null;
  anhoAnterior: number;
  anhoActual: number;
  ventaAnterior: number;
  ventaActual: number;
  variacionAbs: number;
  variacionPct: number | null;
  unidadesAnterior: number;
  unidadesActual: number;
  ticketsAnterior: number;
  ticketsActual: number;
  ticketsVariacionAbs: number;
  ticketsVariacionPct: number | null;
};

type ComparativoOfflineMesRow = ComparativoOfflineBaseRow & {
  nmes: number;
};

type ComparativoOfflineDiaRow = ComparativoOfflineBaseRow & {
  nmes: number;
  ndia: number;
};

type ComparativoOfflineSemanaRow = ComparativoOfflineBaseRow & {
  semanaIso: number;
};

type ComparativoOfflineMesRangoRow = {
  nmes: number;
  anhoAnterior: number;
  anhoActual: number;
  fechaDesdeAnterior: string | null;
  fechaHastaAnterior: string | null;
  fechaDesdeActual: string | null;
  fechaHastaActual: string | null;
};

type ComparativoOfflineAnyRow =
  | ComparativoOfflineMesRow
  | ComparativoOfflineDiaRow
  | ComparativoOfflineSemanaRow;
type ExportTarget = "activa" | TabId | "all";

type SyncStatusLite = {
  finIso?: string | null;
  inicioIso?: string | null;
};

type SyncHistorialLite = {
  fin?: string | null;
  inicio?: string | null;
};

type DiagnosticoSincronizacionLite = {
  estado?: SyncStatusLite | null;
  ultimo?: SyncHistorialLite | null;
};

type TabStateMap = Record<TabId, boolean>;
type TabErrorMap = Record<TabId, string | null>;
type ConsumoViewMode = "Por Hora" | "Por Plataforma" | "Por Marca" | "Por Sucursal";
type MesSortField =
  | "sucursal"
  | "ventaAnterior"
  | "ventaActual"
  | "variacionAbs"
  | "variacionPct"
  | "ticketsAnterior"
  | "ticketsActual"
  | "ticketsVariacionAbs";
type SortDirection = "asc" | "desc";

const TAB_CONFIG: Record<
  TabId,
  {
    label: string;
    icon: string;
    endpoint: string;
    periodHeader: string;
    subtitle: string;
  }
> = {
  mes: {
    label: "Mensual",
    icon: "ri-calendar-line",
    endpoint: "/api/informes/ventas/comparativo-offline/mes",
    periodHeader: "Mes",
    subtitle: "Compara mes a mes el año actual contra el anterior.",
  },
  dia: {
    label: "Diario",
    icon: "ri-calendar-check-line",
    endpoint: "/api/informes/ventas/comparativo-offline/dia",
    periodHeader: "Día",
    subtitle: "Compara cada día calendario entre ambos años.",
  },
  semana: {
    label: "Semanal",
    icon: "ri-calendar-2-line",
    endpoint: "/api/informes/ventas/comparativo-offline/semana",
    periodHeader: "Semana",
    subtitle: "Compara semanas ISO del año actual contra el anterior.",
  },
};

const DEFAULT_SERVER = "PARLA_SERVER";
const DEFAULT_LIMIT = 5000;

const MONTH_FULL_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const MONTH_TAB_ITEMS = MONTH_FULL_NAMES.map((label, index) => ({
  value: index + 1,
  label,
}));

const DAY_TAB_ITEMS = Array.from({ length: 31 }, (_, index) => ({
  value: index + 1,
  label: String(index + 1).padStart(2, "0"),
}));

const WEEK_TAB_ITEMS = Array.from({ length: 53 }, (_, index) => ({
  value: index + 1,
  label: String(index + 1).padStart(2, "0"),
}));

const SEMESTER_TAB_ITEMS: Array<{
  value: SemestreId;
  label: string;
}> = [
  { value: 1, label: "Primer Semestre" },
  { value: 2, label: "Segundo Semestre" },
];

const CONSUMO_VIEW_MODE_OPTIONS: ConsumoViewMode[] = [
  "Por Hora",
  "Por Plataforma",
  "Por Marca",
  "Por Sucursal",
];

const getSemesterRange = (semester: SemestreId) =>
  semester === 1
    ? { fromWeek: 1, toWeek: 26 }
    : { fromWeek: 27, toWeek: 53 };

const getIsoWeek = (date: Date) => {
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const day = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  return Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getIsoWeekDateRange = (year: number, week: number) => {
  const january4 = new Date(year, 0, 4);
  const january4Day = january4.getDay() || 7;
  const week1Monday = new Date(year, 0, 4 - january4Day + 1);
  const from = new Date(week1Monday);
  from.setDate(week1Monday.getDate() + (week - 1) * 7);
  const to = new Date(from);
  to.setDate(from.getDate() + 6);
  return {
    desde: toIsoDate(from),
    hasta: toIsoDate(to),
  };
};

const moneyFormat = new Intl.NumberFormat("es-PY", {
  maximumFractionDigits: 0,
});

const numberFormat = new Intl.NumberFormat("es-PY", {
  maximumFractionDigits: 0,
});

const formatCurrency = (value: number) => moneyFormat.format(value ?? 0);
const formatNumber = (value: number) => numberFormat.format(value ?? 0);

const formatDateLabel = (date: Date) =>
  date.toLocaleDateString("es-PY", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const formatDateTimeLabel = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("es-PY");
};

const formatIsoDateLabel = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatDateLabel(parsed);
};

const formatDateRangeLabel = (desde?: string | null, hasta?: string | null) => {
  const desdeLabel = formatIsoDateLabel(desde);
  const hastaLabel = formatIsoDateLabel(hasta);
  if (!desdeLabel && !hastaLabel) return "Sin datos";
  if (desdeLabel && hastaLabel) return `${desdeLabel} al ${hastaLabel}`;
  return desdeLabel ?? hastaLabel ?? "Sin datos";
};

const parseIsoDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatVariationPct = (value: number | null) => {
  if (value === null || value === undefined) return "-";
  return `${value.toFixed(1)}%`;
};

const formatSubdivision = (value?: string | null) => {
  const cleaned = String(value ?? "").trim();
  return cleaned.length > 0 ? cleaned : "—";
};

const SUBDIVISION_COLUMN_STYLE: React.CSSProperties = {
  width: 220,
  minWidth: 180,
  maxWidth: 220,
  verticalAlign: "middle",
};

const SUBDIVISION_CONTENT_STYLE: React.CSSProperties = {
  maxWidth: 220,
};

const renderSubdivisionContent = (value?: string | null) => {
  const text = formatSubdivision(value);
  return (
    <span
      className="d-block text-truncate"
      style={SUBDIVISION_CONTENT_STYLE}
      title={text === "—" ? undefined : text}
    >
      {text}
    </span>
  );
};

const variationColor = (value: number) => {
  if (value > 0) return "success";
  if (value < 0) return "danger";
  return "secondary";
};

const normalizeSubdivision = (value?: string | null) =>
  String(value ?? "").trim().toUpperCase();

const normalizeSucursal = (value?: string | null) =>
  String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

const esSucursalPlantaProduccion = (value?: string | null) =>
  normalizeSucursal(value).startsWith("PLANTA DE PRODUCCION");

const agruparRowsPorSucursal = <T extends ComparativoOfflineAnyRow>(rows: T[]): T[] => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const mapa = new Map<string, T>();
  rows.forEach((row) => {
    const key = `${row.idsucursal ?? "null"}|${normalizeSucursal(row.sucursal)}`;
    const existente = mapa.get(key);
    if (!existente) {
      mapa.set(key, {
        ...row,
        subdivision: null,
        ventaAnterior: Number(row.ventaAnterior ?? 0),
        ventaActual: Number(row.ventaActual ?? 0),
        variacionAbs: Number(row.variacionAbs ?? 0),
        variacionPct: row.variacionPct ?? null,
        unidadesAnterior: Number(row.unidadesAnterior ?? 0),
        unidadesActual: Number(row.unidadesActual ?? 0),
        ticketsAnterior: Number(row.ticketsAnterior ?? 0),
        ticketsActual: Number(row.ticketsActual ?? 0),
        ticketsVariacionAbs: Number(row.ticketsVariacionAbs ?? 0),
        ticketsVariacionPct: row.ticketsVariacionPct ?? null,
      } as T);
      return;
    }

    existente.ventaAnterior += Number(row.ventaAnterior ?? 0);
    existente.ventaActual += Number(row.ventaActual ?? 0);
    existente.unidadesAnterior += Number(row.unidadesAnterior ?? 0);
    existente.unidadesActual += Number(row.unidadesActual ?? 0);
    existente.ticketsAnterior += Number(row.ticketsAnterior ?? 0);
    existente.ticketsActual += Number(row.ticketsActual ?? 0);
    existente.variacionAbs =
      Number(existente.ventaActual) - Number(existente.ventaAnterior);
    existente.variacionPct =
      Number(existente.ventaAnterior) === 0
        ? null
        : (Number(existente.variacionAbs) / Number(existente.ventaAnterior)) * 100;
    existente.ticketsVariacionAbs =
      Number(existente.ticketsActual) - Number(existente.ticketsAnterior);
    existente.ticketsVariacionPct =
      Number(existente.ticketsAnterior) === 0
        ? null
        : (Number(existente.ticketsVariacionAbs) / Number(existente.ticketsAnterior)) *
          100;
  });

  return Array.from(mapa.values());
};

const compararRowsComparativo = (
  a: ComparativoOfflineBaseRow,
  b: ComparativoOfflineBaseRow,
  campo: MesSortField,
) => {
  if (campo === "sucursal") {
    const comparacionSucursal = a.sucursal.localeCompare(b.sucursal, "es", {
      sensitivity: "base",
    });
    if (comparacionSucursal !== 0) {
      return comparacionSucursal;
    }
    return normalizeSubdivision(a.subdivision).localeCompare(
      normalizeSubdivision(b.subdivision),
      "es",
      { sensitivity: "base" },
    );
  }
  if (campo === "ventaAnterior") {
    return Number(a.ventaAnterior) - Number(b.ventaAnterior);
  }
  if (campo === "ventaActual") {
    return Number(a.ventaActual) - Number(b.ventaActual);
  }
  if (campo === "variacionPct") {
    return Number(a.variacionPct ?? -99999) - Number(b.variacionPct ?? -99999);
  }
  if (campo === "ticketsAnterior") {
    return Number(a.ticketsAnterior) - Number(b.ticketsAnterior);
  }
  if (campo === "ticketsActual") {
    return Number(a.ticketsActual) - Number(b.ticketsActual);
  }
  if (campo === "ticketsVariacionAbs") {
    return Number(a.ticketsVariacionAbs) - Number(b.ticketsVariacionAbs);
  }
  return Number(a.variacionAbs) - Number(b.variacionAbs);
};

const buildRowKey = (tab: TabId, row: ComparativoOfflineAnyRow) => {
  const subdivisionKey = normalizeSubdivision(row.subdivision);
  if (tab === "mes") {
    const typed = row as ComparativoOfflineMesRow;
    return `${typed.idsucursal ?? "s"}-${typed.sucursal}-${subdivisionKey}-${typed.nmes}`;
  }
  if (tab === "dia") {
    const typed = row as ComparativoOfflineDiaRow;
    return `${typed.idsucursal ?? "s"}-${typed.sucursal}-${subdivisionKey}-${typed.nmes}-${typed.ndia}`;
  }
  const typed = row as ComparativoOfflineSemanaRow;
  return `${typed.idsucursal ?? "s"}-${typed.sucursal}-${subdivisionKey}-${typed.semanaIso}`;
};

const buildPeriodoKey = (tab: TabId, row: ComparativoOfflineAnyRow) => {
  if (tab === "mes") {
    const typed = row as ComparativoOfflineMesRow;
    return `m-${typed.nmes}`;
  }
  if (tab === "dia") {
    const typed = row as ComparativoOfflineDiaRow;
    return `d-${typed.nmes}-${typed.ndia}`;
  }
  const typed = row as ComparativoOfflineSemanaRow;
  return `s-${typed.semanaIso}`;
};

const consolidarComparativoRows = <T extends ComparativoOfflineAnyRow>(
  tab: TabId,
  rows: T[],
): T[] => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const filasUnicas: T[] = [];
  const fingerprints = new Set<string>();
  rows.forEach((row) => {
    const periodo = buildPeriodoKey(tab, row);
    const sucursalKey = String(row.sucursal ?? "").trim().toUpperCase();
    const subdivisionKey = normalizeSubdivision(row.subdivision);
    const fingerprint = [
      row.idsucursal ?? "null",
      sucursalKey,
      subdivisionKey,
      periodo,
      row.anhoAnterior,
      row.anhoActual,
      Number(row.ventaAnterior ?? 0),
      Number(row.ventaActual ?? 0),
      Number(row.variacionAbs ?? 0),
      row.variacionPct ?? "null",
      Number(row.unidadesAnterior ?? 0),
      Number(row.unidadesActual ?? 0),
      Number(row.ticketsAnterior ?? 0),
      Number(row.ticketsActual ?? 0),
      Number(row.ticketsVariacionAbs ?? 0),
      row.ticketsVariacionPct ?? "null",
    ].join("|");
    if (fingerprints.has(fingerprint)) {
      return;
    }
    fingerprints.add(fingerprint);
    filasUnicas.push(row);
  });

  const mapa = new Map<string, ComparativoOfflineAnyRow>();
  filasUnicas.forEach((row) => {
    const periodo = buildPeriodoKey(tab, row);
    const sucursalKey = String(row.sucursal ?? "").trim().toUpperCase();
    const subdivisionKey = normalizeSubdivision(row.subdivision);
    const key = `${row.idsucursal ?? "null"}|${sucursalKey}|${subdivisionKey}|${periodo}`;
    const actual = mapa.get(key);
    if (!actual) {
      mapa.set(key, { ...row });
      return;
    }

    actual.ventaAnterior += Number(row.ventaAnterior ?? 0);
    actual.ventaActual += Number(row.ventaActual ?? 0);
    actual.unidadesAnterior += Number(row.unidadesAnterior ?? 0);
    actual.unidadesActual += Number(row.unidadesActual ?? 0);
    actual.ticketsAnterior += Number(row.ticketsAnterior ?? 0);
    actual.ticketsActual += Number(row.ticketsActual ?? 0);
    actual.variacionAbs = Number(actual.ventaActual) - Number(actual.ventaAnterior);
    actual.variacionPct =
      Number(actual.ventaAnterior) === 0
        ? null
        : (Number(actual.variacionAbs) / Number(actual.ventaAnterior)) * 100;
    actual.ticketsVariacionAbs =
      Number(actual.ticketsActual) - Number(actual.ticketsAnterior);
    actual.ticketsVariacionPct =
      Number(actual.ticketsAnterior) === 0
        ? null
        : (Number(actual.ticketsVariacionAbs) / Number(actual.ticketsAnterior)) * 100;
  });

  return Array.from(mapa.values()) as T[];
};

type VentasComparativoOfflinePageProps = {
  breadCrumbTitle?: string;
  reportTitle?: string;
  showConsumptionViewModes?: boolean;
};

const VentasComparativoOfflinePage = ({
  breadCrumbTitle = "Ventas",
  reportTitle = "Comparativo de Ventas",
  showConsumptionViewModes = false,
}: VentasComparativoOfflinePageProps) => {
  const [consumoViewMode, setConsumoViewMode] = useState<ConsumoViewMode>("Por Hora");
  const [activeTab, setActiveTab] = useState<TabId>("mes");
  const [serverOptions, setServerOptions] = useState<string[]>([]);
  const [serverSeleccionado, setServerSeleccionado] = useState<string>("");
  const [compararHastaFechaActual, setCompararHastaFechaActual] = useState(true);
  const [mostrarSubdivision, setMostrarSubdivision] = useState(false);
  const [mostrarPlantaProduccion, setMostrarPlantaProduccion] = useState(false);
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1);
  const [diaSeleccionado, setDiaSeleccionado] = useState(new Date().getDate());
  const [semanaSeleccionada, setSemanaSeleccionada] = useState(getIsoWeek(new Date()));
  const [semestreSeleccionado, setSemestreSeleccionado] = useState<SemestreId>(
    getIsoWeek(new Date()) <= 26 ? 1 : 2,
  );
  const [mesSortField, setMesSortField] = useState<MesSortField>("sucursal");
  const [mesSortDirection, setMesSortDirection] = useState<SortDirection>("asc");
  const [diaSortField, setDiaSortField] = useState<MesSortField>("sucursal");
  const [diaSortDirection, setDiaSortDirection] = useState<SortDirection>("asc");
  const [semanaSortField, setSemanaSortField] = useState<MesSortField>("sucursal");
  const [semanaSortDirection, setSemanaSortDirection] = useState<SortDirection>("asc");
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [exportandoPdf, setExportandoPdf] = useState(false);
  const [excelDropdownOpen, setExcelDropdownOpen] = useState(false);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [syncDiagnostico, setSyncDiagnostico] =
    useState<DiagnosticoSincronizacionLite | null>(null);

  const [mesRows, setMesRows] = useState<ComparativoOfflineMesRow[]>([]);
  const [mesRangos, setMesRangos] = useState<ComparativoOfflineMesRangoRow[]>([]);
  const [diaRows, setDiaRows] = useState<ComparativoOfflineDiaRow[]>([]);
  const [semanaRows, setSemanaRows] = useState<ComparativoOfflineSemanaRow[]>([]);

  const [loadingByTab, setLoadingByTab] = useState<TabStateMap>({
    mes: false,
    dia: false,
    semana: false,
  });
  const [loadedByTab, setLoadedByTab] = useState<TabStateMap>({
    mes: false,
    dia: false,
    semana: false,
  });
  const [errorByTab, setErrorByTab] = useState<TabErrorMap>({
    mes: null,
    dia: null,
    semana: null,
  });

  const setRowsByTab = useCallback((tab: TabId, rows: ComparativoOfflineAnyRow[]) => {
    const normalizadas = consolidarComparativoRows(
      tab,
      (Array.isArray(rows) ? rows : []) as ComparativoOfflineAnyRow[],
    );
    if (tab === "mes") {
      setMesRows(normalizadas as ComparativoOfflineMesRow[]);
      return;
    }
    if (tab === "dia") {
      setDiaRows(normalizadas as ComparativoOfflineDiaRow[]);
      return;
    }
    setSemanaRows(normalizadas as ComparativoOfflineSemanaRow[]);
  }, []);

  const buildQueryString = useCallback(() => {
    if (!serverSeleccionado) {
      return "";
    }
    const params = new URLSearchParams();
    params.set("server", serverSeleccionado);
    params.set("limit", String(DEFAULT_LIMIT));
    return params.toString();
  }, [serverSeleccionado]);

  const fetchTab = useCallback(
    async (tab: TabId) => {
      if (!serverSeleccionado) {
        return;
      }
      setLoadingByTab((prev) => ({ ...prev, [tab]: true }));
      setErrorByTab((prev) => ({ ...prev, [tab]: null }));

      try {
        const query = buildQueryString();
        const endpoint = TAB_CONFIG[tab].endpoint;
        const url = query ? `${endpoint}?${query}` : endpoint;

        if (tab === "mes") {
          const rangosUrl = query
            ? "/api/informes/ventas/comparativo-offline/mes/rangos?" + query
            : "/api/informes/ventas/comparativo-offline/mes/rangos";
          const [data, rangos] = await Promise.all([
            requestJson<ComparativoOfflineAnyRow[]>(url),
            requestJson<ComparativoOfflineMesRangoRow[]>(rangosUrl),
          ]);
          setRowsByTab(tab, Array.isArray(data) ? data : []);
          setMesRangos(Array.isArray(rangos) ? rangos : []);
        } else {
          const data = await requestJson<ComparativoOfflineAnyRow[]>(url);
          setRowsByTab(tab, Array.isArray(data) ? data : []);
        }
        setLoadedByTab((prev) => ({ ...prev, [tab]: true }));
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "No se pudo cargar el comparativo offline.";
        setRowsByTab(tab, []);
        if (tab === "mes") {
          setMesRangos([]);
        }
        setErrorByTab((prev) => ({ ...prev, [tab]: message }));
        setLoadedByTab((prev) => ({ ...prev, [tab]: true }));
      } finally {
        setLoadingByTab((prev) => ({ ...prev, [tab]: false }));
      }
    },
    [buildQueryString, serverSeleccionado, setRowsByTab],
  );

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
  };

  const handleServerChange = (server: string) => {
    const nextServer = server.trim().toUpperCase();
    if (!nextServer || nextServer === serverSeleccionado) {
      return;
    }
    setServerSeleccionado(nextServer);
    setMesRows([]);
    setMesRangos([]);
    setDiaRows([]);
    setSemanaRows([]);
    setLoadedByTab({
      mes: false,
      dia: false,
      semana: false,
    });
    setErrorByTab({
      mes: null,
      dia: null,
      semana: null,
    });
    setLoadingByTab({
      mes: false,
      dia: false,
      semana: false,
    });
  };

  useEffect(() => {
    let activo = true;
    const fetchServerOptions = async () => {
      try {
        const respuesta = await requestJson<{ servidores?: string[] }>(
          "/api/informes/ventas/comparativo-offline/filtros",
        );
        if (!activo) return;
        const servidores = Array.isArray(respuesta?.servidores)
          ? respuesta.servidores
              .map((item) => String(item ?? "").trim().toUpperCase())
              .filter((item) => item.length > 0)
          : [];
        const unicos = Array.from(new Set(servidores));
        const finalOptions =
          unicos.length > 0 ? unicos : [DEFAULT_SERVER];
        setServerOptions(finalOptions);
        setServerSeleccionado((prev) => {
          if (prev && finalOptions.includes(prev)) {
            return prev;
          }
          if (finalOptions.includes(DEFAULT_SERVER)) {
            return DEFAULT_SERVER;
          }
          return finalOptions[0] ?? "";
        });
      } catch {
        if (!activo) return;
        setServerOptions([DEFAULT_SERVER]);
        setServerSeleccionado(DEFAULT_SERVER);
      }
    };
    void fetchServerOptions();
    return () => {
      activo = false;
    };
  }, []);

  useEffect(() => {
    let activo = true;
    const cargarLogo = async () => {
      try {
        const fuentes = [baconLogo, "/baconappen_529x124.png", "/baconappen.png"];
        for (const fuente of fuentes) {
          if (!fuente) {
            continue;
          }
          try {
            const respuesta = await fetch(fuente);
            if (!respuesta.ok) {
              continue;
            }
            const blob = await respuesta.blob();
            const lector = new FileReader();
            const resultado = await new Promise<string>((resolve, reject) => {
              lector.onloadend = () => {
                if (typeof lector.result === "string") {
                  resolve(lector.result);
                } else {
                  reject(new Error("No pudimos leer el logo."));
                }
              };
              lector.onerror = () => reject(new Error("No pudimos leer el logo."));
              lector.readAsDataURL(blob);
            });
            if (activo) {
              setLogoDataUrl(resultado);
            }
            return;
          } catch {
            continue;
          }
        }
      } catch {
        // ignorado
      }
    };
    void cargarLogo();
    return () => {
      activo = false;
    };
  }, []);

  useEffect(() => {
    if (!serverSeleccionado) {
      return;
    }
    (Object.keys(TAB_CONFIG) as TabId[]).forEach((tab) => {
      if (!loadedByTab[tab] && !loadingByTab[tab]) {
        void fetchTab(tab);
      }
    });
  }, [fetchTab, loadedByTab, loadingByTab, serverSeleccionado]);

  useEffect(() => {
    if (!serverSeleccionado) {
      setSyncDiagnostico(null);
      return;
    }
    let activo = true;
    setSyncDiagnostico(null);
    const cargarDiagnostico = async () => {
      try {
        const params = new URLSearchParams();
        params.set("server", serverSeleccionado);
        const respuesta = await requestJson<DiagnosticoSincronizacionLite>(
          `/api/informes/sincronizacion/diagnostico?${params.toString()}`,
        );
        if (!activo) {
          return;
        }
        setSyncDiagnostico(respuesta ?? null);
      } catch {
        if (!activo) {
          return;
        }
        setSyncDiagnostico(null);
      }
    };
    void cargarDiagnostico();
    return () => {
      activo = false;
    };
  }, [serverSeleccionado]);

  useEffect(() => {
    if (activeTab !== "mes" || !compararHastaFechaActual || !serverSeleccionado) {
      return;
    }
    if (!loadedByTab.dia && !loadingByTab.dia) {
      void fetchTab("dia");
    }
  }, [
    activeTab,
    compararHastaFechaActual,
    fetchTab,
    loadedByTab.dia,
    loadingByTab.dia,
    serverSeleccionado,
  ]);

  const currentRows = useMemo(() => {
    if (activeTab === "mes") return mesRows as ComparativoOfflineAnyRow[];
    if (activeTab === "dia") return diaRows as ComparativoOfflineAnyRow[];
    return semanaRows as ComparativoOfflineAnyRow[];
  }, [activeTab, mesRows, diaRows, semanaRows]);

  const mesRowsOrdenados = useMemo(() => {
    const filtradas = mostrarPlantaProduccion
      ? mesRows
      : mesRows.filter((row) => !esSucursalPlantaProduccion(row.sucursal));
    return [...filtradas].sort((a, b) => {
      if (a.nmes !== b.nmes) return a.nmes - b.nmes;
      const comparacionSucursal = a.sucursal.localeCompare(b.sucursal, "es", {
        sensitivity: "base",
      });
      if (comparacionSucursal !== 0) return comparacionSucursal;
      return normalizeSubdivision(a.subdivision).localeCompare(
        normalizeSubdivision(b.subdivision),
        "es",
        { sensitivity: "base" },
      );
    });
  }, [mesRows, mostrarPlantaProduccion]);

  const mesesConDatos = useMemo(() => {
    return new Set(mesRowsOrdenados.map((row) => row.nmes));
  }, [mesRowsOrdenados]);

  const mesFilasBaseSeleccionadas = useMemo(() => {
    return mesRowsOrdenados.filter((row) => row.nmes === mesSeleccionado);
  }, [mesRowsOrdenados, mesSeleccionado]);

  const yearFallback = new Date().getFullYear();
  const anhoAnterior = currentRows[0]?.anhoAnterior ?? yearFallback - 1;
  const anhoActual = currentRows[0]?.anhoActual ?? yearFallback;
  const rangoMensualSeleccionado = useMemo(() => {
    const exacto = mesRangos.find(
      (item) =>
        item.nmes === mesSeleccionado &&
        item.anhoAnterior === anhoAnterior &&
        item.anhoActual === anhoActual,
    );
    if (exacto) {
      return exacto;
    }
    return mesRangos.find((item) => item.nmes === mesSeleccionado) ?? null;
  }, [anhoActual, anhoAnterior, mesRangos, mesSeleccionado]);

  const diaCorteActual = useMemo(() => {
    const fechaHastaActual = rangoMensualSeleccionado?.fechaHastaActual;
    const parsed = parseIsoDate(fechaHastaActual);
    return parsed ? parsed.getDate() : null;
  }, [rangoMensualSeleccionado]);

  const mesActualIncompleto = useMemo(() => {
    const fechaHastaActual = rangoMensualSeleccionado?.fechaHastaActual;
    if (!fechaHastaActual) {
      return false;
    }
    const anhoMes = rangoMensualSeleccionado?.anhoActual ?? anhoActual;
    const finDeMes = new Date(anhoMes, mesSeleccionado, 0);
    return fechaHastaActual < toIsoDate(finDeMes);
  }, [anhoActual, mesSeleccionado, rangoMensualSeleccionado]);

  const puedeCompararHastaFecha = mesActualIncompleto && diaCorteActual !== null;
  const comparacionHastaFechaCargando =
    compararHastaFechaActual && !loadedByTab.dia && loadingByTab.dia;

  const usarComparacionHastaFecha =
    compararHastaFechaActual &&
    puedeCompararHastaFecha &&
    loadedByTab.dia;

  const mesFilasSeleccionadas = useMemo(() => {
    if (!usarComparacionHastaFecha || diaCorteActual === null) {
      return mesFilasBaseSeleccionadas;
    }
    const mapa = new Map<string, ComparativoOfflineMesRow>();
    const diaRowsFuente = mostrarPlantaProduccion
      ? diaRows
      : diaRows.filter((row) => !esSucursalPlantaProduccion(row.sucursal));
    diaRowsFuente
      .filter(
        (row) => row.nmes === mesSeleccionado && Number(row.ndia ?? 0) <= diaCorteActual,
      )
      .forEach((row) => {
        const key = `${row.idsucursal ?? "null"}::${row.sucursal}::${normalizeSubdivision(
          row.subdivision,
        )}`;
        const actual = mapa.get(key);
        if (!actual) {
          mapa.set(key, {
            idsucursal: row.idsucursal,
            sucursal: row.sucursal,
            subdivision: row.subdivision ?? null,
            nmes: mesSeleccionado,
            anhoAnterior: row.anhoAnterior,
            anhoActual: row.anhoActual,
            ventaAnterior: Number(row.ventaAnterior ?? 0),
            ventaActual: Number(row.ventaActual ?? 0),
            variacionAbs: Number(row.variacionAbs ?? 0),
            variacionPct: row.variacionPct ?? null,
            unidadesAnterior: Number(row.unidadesAnterior ?? 0),
            unidadesActual: Number(row.unidadesActual ?? 0),
            ticketsAnterior: Number(row.ticketsAnterior ?? 0),
            ticketsActual: Number(row.ticketsActual ?? 0),
            ticketsVariacionAbs: Number(row.ticketsVariacionAbs ?? 0),
            ticketsVariacionPct: row.ticketsVariacionPct ?? null,
          });
          return;
        }
        actual.ventaAnterior += Number(row.ventaAnterior ?? 0);
        actual.ventaActual += Number(row.ventaActual ?? 0);
        actual.variacionAbs += Number(row.variacionAbs ?? 0);
        actual.unidadesAnterior += Number(row.unidadesAnterior ?? 0);
        actual.unidadesActual += Number(row.unidadesActual ?? 0);
        actual.ticketsAnterior += Number(row.ticketsAnterior ?? 0);
        actual.ticketsActual += Number(row.ticketsActual ?? 0);
      });
    const filas = Array.from(mapa.values()).map((row) => {
      const variacionAbs = Number(row.ventaActual ?? 0) - Number(row.ventaAnterior ?? 0);
      const variacionPct =
        Number(row.ventaAnterior ?? 0) === 0
          ? null
          : (variacionAbs / Number(row.ventaAnterior ?? 0)) * 100;
      const ticketsVariacionAbs =
        Number(row.ticketsActual ?? 0) - Number(row.ticketsAnterior ?? 0);
      const ticketsVariacionPct =
        Number(row.ticketsAnterior ?? 0) === 0
          ? null
          : (ticketsVariacionAbs / Number(row.ticketsAnterior ?? 0)) * 100;
      return {
        ...row,
        variacionAbs,
        variacionPct,
        ticketsVariacionAbs,
        ticketsVariacionPct,
      };
    });
    return filas.length > 0 ? filas : mesFilasBaseSeleccionadas;
  }, [
    diaCorteActual,
    diaRows,
    mesFilasBaseSeleccionadas,
    mesSeleccionado,
    mostrarPlantaProduccion,
    usarComparacionHastaFecha,
  ]);

  const mesTieneSubdivision = useMemo(
    () =>
      mesFilasSeleccionadas.some(
        (row) => normalizeSubdivision(row.subdivision).length > 0,
      ),
    [mesFilasSeleccionadas],
  );
  const mesMostrarSubdivision = mostrarSubdivision && mesTieneSubdivision;
  const mesColumnas = mesMostrarSubdivision ? 9 : 8;
  const mesFilasVisibles = useMemo(() => {
    if (mesMostrarSubdivision) {
      return mesFilasSeleccionadas;
    }
    return agruparRowsPorSucursal(mesFilasSeleccionadas);
  }, [mesFilasSeleccionadas, mesMostrarSubdivision]);

  const mesFilasOrdenadas = useMemo(() => {
    const filas = [...mesFilasVisibles];
    filas.sort((a, b) => {
      const comparacion = compararRowsComparativo(a, b, mesSortField);
      return mesSortDirection === "asc" ? comparacion : comparacion * -1;
    });
    return filas;
  }, [mesFilasVisibles, mesSortDirection, mesSortField]);

  const mesTotalesSeleccionados = useMemo(() => {
    return mesFilasVisibles.reduce(
      (acc, row) => {
        acc.ventaAnterior += Number(row.ventaAnterior ?? 0);
        acc.ventaActual += Number(row.ventaActual ?? 0);
        acc.variacionAbs += Number(row.variacionAbs ?? 0);
        acc.ticketsAnterior += Number(row.ticketsAnterior ?? 0);
        acc.ticketsActual += Number(row.ticketsActual ?? 0);
        acc.ticketsVariacionAbs += Number(row.ticketsVariacionAbs ?? 0);
        return acc;
      },
      {
        ventaAnterior: 0,
        ventaActual: 0,
        variacionAbs: 0,
        ticketsAnterior: 0,
        ticketsActual: 0,
        ticketsVariacionAbs: 0,
      },
    );
  }, [mesFilasVisibles]);
  const variacionPctTotalVentas =
    mesTotalesSeleccionados.ventaAnterior === 0
      ? null
      : (mesTotalesSeleccionados.variacionAbs / mesTotalesSeleccionados.ventaAnterior) * 100;

  const diaRowsMesSeleccionado = useMemo(() => {
    const filtradas = mostrarPlantaProduccion
      ? diaRows
      : diaRows.filter((row) => !esSucursalPlantaProduccion(row.sucursal));
    return filtradas.filter((row) => row.nmes === mesSeleccionado);
  }, [diaRows, mesSeleccionado, mostrarPlantaProduccion]);

  const diasConDatosMesSeleccionado = useMemo(() => {
    return new Set(diaRowsMesSeleccionado.map((row) => row.ndia));
  }, [diaRowsMesSeleccionado]);

  const diaFilasSeleccionadas = useMemo(
    () => diaRowsMesSeleccionado.filter((row) => row.ndia === diaSeleccionado),
    [diaRowsMesSeleccionado, diaSeleccionado],
  );

  const diaTieneSubdivision = useMemo(
    () =>
      diaFilasSeleccionadas.some(
        (row) => normalizeSubdivision(row.subdivision).length > 0,
      ),
    [diaFilasSeleccionadas],
  );
  const diaMostrarSubdivision = mostrarSubdivision && diaTieneSubdivision;
  const diaColumnas = diaMostrarSubdivision ? 9 : 8;
  const diaFilasVisibles = useMemo(() => {
    if (diaMostrarSubdivision) {
      return diaFilasSeleccionadas;
    }
    return agruparRowsPorSucursal(diaFilasSeleccionadas);
  }, [diaFilasSeleccionadas, diaMostrarSubdivision]);

  const diaFilasOrdenadas = useMemo(() => {
    const filas = [...diaFilasVisibles];
    filas.sort((a, b) => {
      const comparacion = compararRowsComparativo(a, b, diaSortField);
      return diaSortDirection === "asc" ? comparacion : comparacion * -1;
    });
    return filas;
  }, [diaFilasVisibles, diaSortDirection, diaSortField]);

  const diaTotalesSeleccionados = useMemo(() => {
    return diaFilasVisibles.reduce(
      (acc, row) => {
        acc.ventaAnterior += Number(row.ventaAnterior ?? 0);
        acc.ventaActual += Number(row.ventaActual ?? 0);
        acc.variacionAbs += Number(row.variacionAbs ?? 0);
        acc.ticketsAnterior += Number(row.ticketsAnterior ?? 0);
        acc.ticketsActual += Number(row.ticketsActual ?? 0);
        acc.ticketsVariacionAbs += Number(row.ticketsVariacionAbs ?? 0);
        return acc;
      },
      {
        ventaAnterior: 0,
        ventaActual: 0,
        variacionAbs: 0,
        ticketsAnterior: 0,
        ticketsActual: 0,
        ticketsVariacionAbs: 0,
      },
    );
  }, [diaFilasVisibles]);

  const variacionPctTotalVentasDia =
    diaTotalesSeleccionados.ventaAnterior === 0
      ? null
      : (diaTotalesSeleccionados.variacionAbs / diaTotalesSeleccionados.ventaAnterior) * 100;

  const rangoDiarioAnterior = useMemo(() => {
    const ultimoDiaMesAnterior = new Date(anhoAnterior, mesSeleccionado, 0).getDate();
    if (diaSeleccionado > ultimoDiaMesAnterior) {
      return "Sin datos";
    }
    const fecha = new Date(anhoAnterior, mesSeleccionado - 1, diaSeleccionado);
    const iso = toIsoDate(fecha);
    return formatDateRangeLabel(iso, iso);
  }, [anhoAnterior, diaSeleccionado, mesSeleccionado]);

  const rangoDiarioActual = useMemo(() => {
    const ultimoDiaMesActual = new Date(anhoActual, mesSeleccionado, 0).getDate();
    if (diaSeleccionado > ultimoDiaMesActual) {
      return "Sin datos";
    }
    const fecha = new Date(anhoActual, mesSeleccionado - 1, diaSeleccionado);
    const iso = toIsoDate(fecha);
    return formatDateRangeLabel(iso, iso);
  }, [anhoActual, diaSeleccionado, mesSeleccionado]);

  const fechaDiariaActualLabel = useMemo(() => {
    const ultimoDiaMesActual = new Date(anhoActual, mesSeleccionado, 0).getDate();
    if (diaSeleccionado > ultimoDiaMesActual) {
      return null;
    }
    const fecha = new Date(anhoActual, mesSeleccionado - 1, diaSeleccionado);
    return formatDateLabel(fecha);
  }, [anhoActual, diaSeleccionado, mesSeleccionado]);

  const rangoSemanalAnterior = useMemo(() => {
    const rango = getIsoWeekDateRange(anhoAnterior, semanaSeleccionada);
    return formatDateRangeLabel(rango.desde, rango.hasta);
  }, [anhoAnterior, semanaSeleccionada]);

  const rangoSemanalActual = useMemo(() => {
    const rango = getIsoWeekDateRange(anhoActual, semanaSeleccionada);
    return formatDateRangeLabel(rango.desde, rango.hasta);
  }, [anhoActual, semanaSeleccionada]);

  const fechaSemanalActualLabel = useMemo(() => {
    const rango = getIsoWeekDateRange(anhoActual, semanaSeleccionada);
    return formatIsoDateLabel(rango.hasta);
  }, [anhoActual, semanaSeleccionada]);

  const semanaRowsOrdenados = useMemo(() => {
    const filtradas = mostrarPlantaProduccion
      ? semanaRows
      : semanaRows.filter((row) => !esSucursalPlantaProduccion(row.sucursal));
    return [...filtradas].sort((a, b) => {
      if (a.semanaIso !== b.semanaIso) return a.semanaIso - b.semanaIso;
      const comparacionSucursal = a.sucursal.localeCompare(b.sucursal, "es", {
        sensitivity: "base",
      });
      if (comparacionSucursal !== 0) return comparacionSucursal;
      return normalizeSubdivision(a.subdivision).localeCompare(
        normalizeSubdivision(b.subdivision),
        "es",
        { sensitivity: "base" },
      );
    });
  }, [semanaRows, mostrarPlantaProduccion]);

  const semanasConDatos = useMemo(() => {
    return new Set(semanaRowsOrdenados.map((row) => row.semanaIso));
  }, [semanaRowsOrdenados]);
  const semestresConDatos = useMemo<Record<SemestreId, boolean>>(
    () => ({
      1: Array.from(semanasConDatos).some((week) => week >= 1 && week <= 26),
      2: Array.from(semanasConDatos).some((week) => week >= 27 && week <= 53),
    }),
    [semanasConDatos],
  );
  const semanasDelSemestreSeleccionado = useMemo(() => {
    const { fromWeek, toWeek } = getSemesterRange(semestreSeleccionado);
    return WEEK_TAB_ITEMS.filter(
      (item) => item.value >= fromWeek && item.value <= toWeek,
    );
  }, [semestreSeleccionado]);

  const semanaFilasSeleccionadas = useMemo(
    () => semanaRowsOrdenados.filter((row) => row.semanaIso === semanaSeleccionada),
    [semanaRowsOrdenados, semanaSeleccionada],
  );

  const semanaTieneSubdivision = useMemo(
    () =>
      semanaFilasSeleccionadas.some(
        (row) => normalizeSubdivision(row.subdivision).length > 0,
      ),
    [semanaFilasSeleccionadas],
  );
  const semanaMostrarSubdivision = mostrarSubdivision && semanaTieneSubdivision;
  const semanaColumnas = semanaMostrarSubdivision ? 9 : 8;
  const semanaFilasVisibles = useMemo(() => {
    if (semanaMostrarSubdivision) {
      return semanaFilasSeleccionadas;
    }
    return agruparRowsPorSucursal(semanaFilasSeleccionadas);
  }, [semanaFilasSeleccionadas, semanaMostrarSubdivision]);

  const semanaFilasOrdenadas = useMemo(() => {
    const filas = [...semanaFilasVisibles];
    filas.sort((a, b) => {
      const comparacion = compararRowsComparativo(a, b, semanaSortField);
      return semanaSortDirection === "asc" ? comparacion : comparacion * -1;
    });
    return filas;
  }, [semanaFilasVisibles, semanaSortDirection, semanaSortField]);

  const semanaTotalesSeleccionados = useMemo(() => {
    return semanaFilasVisibles.reduce(
      (acc, row) => {
        acc.ventaAnterior += Number(row.ventaAnterior ?? 0);
        acc.ventaActual += Number(row.ventaActual ?? 0);
        acc.variacionAbs += Number(row.variacionAbs ?? 0);
        acc.ticketsAnterior += Number(row.ticketsAnterior ?? 0);
        acc.ticketsActual += Number(row.ticketsActual ?? 0);
        acc.ticketsVariacionAbs += Number(row.ticketsVariacionAbs ?? 0);
        return acc;
      },
      {
        ventaAnterior: 0,
        ventaActual: 0,
        variacionAbs: 0,
        ticketsAnterior: 0,
        ticketsActual: 0,
        ticketsVariacionAbs: 0,
      },
    );
  }, [semanaFilasVisibles]);

  const variacionPctTotalVentasSemana =
    semanaTotalesSeleccionados.ventaAnterior === 0
      ? null
      : (semanaTotalesSeleccionados.variacionAbs / semanaTotalesSeleccionados.ventaAnterior) *
        100;

  useEffect(() => {
    if (activeTab !== "mes") {
      return;
    }
    if (mesesConDatos.size === 0) {
      return;
    }
    if (!mesesConDatos.has(mesSeleccionado)) {
      const primerMesConDatos = Array.from(mesesConDatos).sort((a, b) => a - b)[0];
      if (primerMesConDatos) {
        setMesSeleccionado(primerMesConDatos);
      }
    }
  }, [activeTab, mesSeleccionado, mesesConDatos]);

  useEffect(() => {
    if (activeTab !== "dia") {
      return;
    }
    if (diasConDatosMesSeleccionado.size === 0) {
      return;
    }
    if (!diasConDatosMesSeleccionado.has(diaSeleccionado)) {
      const primerDiaConDatos = Array.from(diasConDatosMesSeleccionado).sort((a, b) => a - b)[0];
      if (primerDiaConDatos) {
        setDiaSeleccionado(primerDiaConDatos);
      }
    }
  }, [activeTab, diaSeleccionado, diasConDatosMesSeleccionado]);

  useEffect(() => {
    if (activeTab !== "semana") {
      return;
    }
    if (semanasConDatos.size === 0) {
      return;
    }
    if (!semestresConDatos[semestreSeleccionado]) {
      const siguienteSemestre = semestresConDatos[1] ? 1 : semestresConDatos[2] ? 2 : null;
      if (siguienteSemestre && siguienteSemestre !== semestreSeleccionado) {
        setSemestreSeleccionado(siguienteSemestre);
      }
    }
  }, [activeTab, semestreSeleccionado, semestresConDatos, semanasConDatos.size]);

  useEffect(() => {
    if (activeTab !== "semana") {
      return;
    }
    if (semanasConDatos.size === 0) {
      return;
    }
    if (!semanasConDatos.has(semanaSeleccionada)) {
      const semanasOrdenadas = Array.from(semanasConDatos).sort((a, b) => a - b);
      const { fromWeek, toWeek } = getSemesterRange(semestreSeleccionado);
      const primeraSemanaConDatosSemestre = semanasOrdenadas.find(
        (week) => week >= fromWeek && week <= toWeek,
      );
      const primeraSemanaConDatosGlobal = semanasOrdenadas[0];
      const semanaObjetivo =
        primeraSemanaConDatosSemestre ?? primeraSemanaConDatosGlobal;
      if (semanaObjetivo) {
        setSemanaSeleccionada(semanaObjetivo);
        setSemestreSeleccionado(semanaObjetivo <= 26 ? 1 : 2);
      }
    }
  }, [activeTab, semanaSeleccionada, semanasConDatos, semestreSeleccionado]);

  const rangoMensualAnterior = useMemo(
    () => {
      if (!usarComparacionHastaFecha || diaCorteActual === null) {
        return formatDateRangeLabel(
          rangoMensualSeleccionado?.fechaDesdeAnterior,
          rangoMensualSeleccionado?.fechaHastaAnterior,
        );
      }
      const anhoMes = rangoMensualSeleccionado?.anhoAnterior ?? anhoAnterior;
      const ultimoDiaMesAnterior = new Date(anhoMes, mesSeleccionado, 0).getDate();
      const fechaObjetivo = new Date(
        anhoMes,
        mesSeleccionado - 1,
        Math.min(diaCorteActual, ultimoDiaMesAnterior),
      );
      const limite = toIsoDate(fechaObjetivo);
      const hastaOriginal = rangoMensualSeleccionado?.fechaHastaAnterior;
      const hastaAjustada =
        hastaOriginal && hastaOriginal < limite ? hastaOriginal : limite;
      return formatDateRangeLabel(
        rangoMensualSeleccionado?.fechaDesdeAnterior,
        hastaAjustada,
      );
    },
    [
      anhoAnterior,
      diaCorteActual,
      mesSeleccionado,
      rangoMensualSeleccionado,
      usarComparacionHastaFecha,
    ],
  );
  const rangoMensualActual = useMemo(
    () =>
      formatDateRangeLabel(
        rangoMensualSeleccionado?.fechaDesdeActual,
        rangoMensualSeleccionado?.fechaHastaActual,
      ),
    [rangoMensualSeleccionado],
  );
  const fechaCorteActualLabel = useMemo(
    () => formatIsoDateLabel(rangoMensualSeleccionado?.fechaHastaActual),
    [rangoMensualSeleccionado],
  );
  const badgeCorteTexto = useMemo(() => {
    if (!compararHastaFechaActual) {
      return null;
    }
    if (comparacionHastaFechaCargando) {
      return "Corte activo: calculando...";
    }
    if (usarComparacionHastaFecha) {
      return fechaCorteActualLabel
        ? `Corte activo hasta ${fechaCorteActualLabel}`
        : "Corte activo";
    }
    if (!puedeCompararHastaFecha) {
      return "Corte no aplica (mes completo)";
    }
    return "Corte pendiente";
  }, [
    compararHastaFechaActual,
    comparacionHastaFechaCargando,
    fechaCorteActualLabel,
    puedeCompararHastaFecha,
    usarComparacionHastaFecha,
  ]);
  const ultimaSincronizacionIso = useMemo(
    () =>
      syncDiagnostico?.ultimo?.fin ??
      syncDiagnostico?.estado?.finIso ??
      syncDiagnostico?.ultimo?.inicio ??
      syncDiagnostico?.estado?.inicioIso ??
      null,
    [syncDiagnostico],
  );
  const ultimaSincronizacionLabel = useMemo(
    () => formatDateTimeLabel(ultimaSincronizacionIso),
    [ultimaSincronizacionIso],
  );
  const resaltarFechaCorteActualEnTitulo =
    compararHastaFechaActual && Boolean(fechaCorteActualLabel);
  const resaltarFechaCorteDiarioEnTitulo =
    compararHastaFechaActual && Boolean(fechaDiariaActualLabel);
  const resaltarFechaSemanalActualEnTitulo = Boolean(fechaSemanalActualLabel);
  const estaCargandoTabActivo =
    loadingByTab[activeTab] ||
    (activeTab === "mes" && comparacionHastaFechaCargando);

  const cambiarOrdenMensual = (campo: MesSortField) => {
    if (mesSortField === campo) {
      setMesSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setMesSortField(campo);
    setMesSortDirection("asc");
  };

  const cambiarOrdenDiario = (campo: MesSortField) => {
    if (diaSortField === campo) {
      setDiaSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setDiaSortField(campo);
    setDiaSortDirection("asc");
  };

  const cambiarOrdenSemanal = (campo: MesSortField) => {
    if (semanaSortField === campo) {
      setSemanaSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSemanaSortField(campo);
    setSemanaSortDirection("asc");
  };

  const cambiarSemestreSemanal = (semester: SemestreId) => {
    if (!semestresConDatos[semester]) {
      return;
    }
    setSemestreSeleccionado(semester);
    const { fromWeek, toWeek } = getSemesterRange(semester);
    if (semanaSeleccionada >= fromWeek && semanaSeleccionada <= toWeek) {
      return;
    }
    const primeraSemanaConDatosSemestre = WEEK_TAB_ITEMS.find(
      (item) =>
        item.value >= fromWeek &&
        item.value <= toWeek &&
        semanasConDatos.has(item.value),
    )?.value;
    setSemanaSeleccionada(primeraSemanaConDatosSemestre ?? fromWeek);
  };

  const seleccionarSemanaSemanal = (week: number) => {
    setSemanaSeleccionada(week);
    setSemestreSeleccionado(week <= 26 ? 1 : 2);
  };

  const iconoOrden = (
    campo: MesSortField,
    sortField: MesSortField,
    sortDirection: SortDirection,
  ) => {
    if (sortField !== campo) {
      return <i className="ri-arrow-up-down-line ms-1 text-muted" />;
    }
    return sortDirection === "asc" ? (
      <i className="ri-arrow-up-s-line ms-1 text-primary" />
    ) : (
      <i className="ri-arrow-down-s-line ms-1 text-primary" />
    );
  };

  const iconoOrdenMensual = (campo: MesSortField) =>
    iconoOrden(campo, mesSortField, mesSortDirection);

  const iconoOrdenDiario = (campo: MesSortField) =>
    iconoOrden(campo, diaSortField, diaSortDirection);

  const iconoOrdenSemanal = (campo: MesSortField) =>
    iconoOrden(campo, semanaSortField, semanaSortDirection);

  const subdivisionDisponibleEnTabActiva =
    activeTab === "mes"
      ? mesTieneSubdivision
      : activeTab === "dia"
        ? diaTieneSubdivision
        : semanaTieneSubdivision;

  const exportConfigByTab = useMemo<Record<TabId, VentasComparativoExportConfig>>(() => {
    const semestreLabel = semestreSeleccionado === 1 ? "Primer Semestre" : "Segundo Semestre";
    return {
      mes: {
        tab: "mes",
        rows: mesFilasOrdenadas,
        showSubdivision: mesMostrarSubdivision,
        vistaLabel: "Mensual",
        periodoLabel: MONTH_FULL_NAMES[mesSeleccionado - 1],
        rangoAnterior: rangoMensualAnterior,
        rangoActual: rangoMensualActual,
        corteLabel: usarComparacionHastaFecha
          ? fechaCorteActualLabel
            ? `Corte activo hasta ${fechaCorteActualLabel}`
            : "Corte activo"
          : null,
        totalLabel: `Total ${MONTH_FULL_NAMES[mesSeleccionado - 1]}`,
        total: mesTotalesSeleccionados,
        totalVariacionPct: variacionPctTotalVentas,
        server: serverSeleccionado,
        anhoAnterior,
        anhoActual,
        syncDateTimeLabel: ultimaSincronizacionLabel,
      },
      dia: {
        tab: "dia",
        rows: diaFilasOrdenadas,
        showSubdivision: diaMostrarSubdivision,
        vistaLabel: "Diario",
        periodoLabel: `Día ${String(diaSeleccionado).padStart(2, "0")} - ${MONTH_FULL_NAMES[mesSeleccionado - 1]}`,
        rangoAnterior: rangoDiarioAnterior,
        rangoActual: rangoDiarioActual,
        corteLabel: fechaDiariaActualLabel ? `al ${fechaDiariaActualLabel}` : null,
        totalLabel: `Total día ${String(diaSeleccionado).padStart(2, "0")} - ${MONTH_FULL_NAMES[mesSeleccionado - 1]}`,
        total: diaTotalesSeleccionados,
        totalVariacionPct: variacionPctTotalVentasDia,
        server: serverSeleccionado,
        anhoAnterior,
        anhoActual,
        syncDateTimeLabel: ultimaSincronizacionLabel,
      },
      semana: {
        tab: "semana",
        rows: semanaFilasOrdenadas,
        showSubdivision: semanaMostrarSubdivision,
        vistaLabel: "Semanal",
        periodoLabel: `Semana ${String(semanaSeleccionada).padStart(2, "0")} (${semestreLabel})`,
        rangoAnterior: rangoSemanalAnterior,
        rangoActual: rangoSemanalActual,
        corteLabel: fechaSemanalActualLabel ? `al ${fechaSemanalActualLabel}` : null,
        totalLabel: `Total Semana ${String(semanaSeleccionada).padStart(2, "0")}`,
        total: semanaTotalesSeleccionados,
        totalVariacionPct: variacionPctTotalVentasSemana,
        server: serverSeleccionado,
        anhoAnterior,
        anhoActual,
        syncDateTimeLabel: ultimaSincronizacionLabel,
      },
    };
  }, [
    anhoActual,
    anhoAnterior,
    diaFilasOrdenadas,
    diaMostrarSubdivision,
    diaTieneSubdivision,
    diaSeleccionado,
    diaTotalesSeleccionados,
    fechaCorteActualLabel,
    fechaDiariaActualLabel,
    fechaSemanalActualLabel,
    mesFilasOrdenadas,
    mesMostrarSubdivision,
    mesTieneSubdivision,
    mesSeleccionado,
    mesTotalesSeleccionados,
    rangoDiarioActual,
    rangoDiarioAnterior,
    rangoMensualActual,
    rangoMensualAnterior,
    rangoSemanalActual,
    rangoSemanalAnterior,
    semanaFilasOrdenadas,
    semanaMostrarSubdivision,
    semanaTieneSubdivision,
    semanaSeleccionada,
    semanaTotalesSeleccionados,
    semestreSeleccionado,
    serverSeleccionado,
    mostrarSubdivision,
    ultimaSincronizacionLabel,
    usarComparacionHastaFecha,
    variacionPctTotalVentas,
    variacionPctTotalVentasDia,
    variacionPctTotalVentasSemana,
  ]);

  const activeExportConfig = exportConfigByTab[activeTab];
  const exportRowsByTab = useMemo<Record<TabId, boolean>>(
    () => ({
      mes: exportConfigByTab.mes.rows.length > 0,
      dia: exportConfigByTab.dia.rows.length > 0,
      semana: exportConfigByTab.semana.rows.length > 0,
    }),
    [exportConfigByTab],
  );
  const canExportCurrentView = !estaCargandoTabActivo && exportRowsByTab[activeTab];
  const canExportAnyView = Object.values(exportRowsByTab).some(Boolean);

  const getExportConfigsForTarget = (target: ExportTarget) => {
    if (target === "activa") {
      return activeExportConfig.rows.length > 0 ? [activeExportConfig] : [];
    }
    if (target === "all") {
      return (Object.keys(exportConfigByTab) as TabId[])
        .map((tab) => exportConfigByTab[tab])
        .filter((config) => config.rows.length > 0);
    }
    const config = exportConfigByTab[target];
    return config.rows.length > 0 ? [config] : [];
  };

  const handleExportExcel = (target: ExportTarget) => {
    if (exportandoExcel) {
      return;
    }
    const configs = getExportConfigsForTarget(target);
    if (configs.length === 0) {
      return;
    }
    setExportandoExcel(true);
    try {
      if (target === "all") {
        exportVentasComparativoExcelBatch(configs);
      } else {
        exportVentasComparativoExcel(configs[0]);
      }
    } finally {
      setExportandoExcel(false);
      setExcelDropdownOpen(false);
    }
  };

  const handleExportPdf = (target: ExportTarget) => {
    if (exportandoPdf) {
      return;
    }
    const configs = getExportConfigsForTarget(target);
    if (configs.length === 0) {
      return;
    }
    setExportandoPdf(true);
    try {
      configs.forEach((config) => {
        exportVentasComparativoPdf({
          ...config,
          logoDataUrl,
        });
      });
    } finally {
      setExportandoPdf(false);
      setPdfDropdownOpen(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <BreadCrumb title={breadCrumbTitle} pageTitle="Informes" />

        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-transparent">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div className="d-flex flex-column gap-2">
                <div>
                  <h5 className="mb-1">{reportTitle}</h5>
                  <p className="text-muted mb-0">{TAB_CONFIG[activeTab].subtitle}</p>
                </div>
                {showConsumptionViewModes ? (
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <ButtonGroup>
                      {CONSUMO_VIEW_MODE_OPTIONS.map((option) => (
                        <Button
                          key={option}
                          type="button"
                          color="dark"
                          active={consumoViewMode === option}
                          className={`btn-soft-dark mb-0 ${
                            consumoViewMode === option ? "active" : ""
                          }`}
                          onClick={() => setConsumoViewMode(option)}
                        >
                          {option}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </div>
                ) : null}
              </div>
              <div className="d-flex align-items-end gap-2 flex-wrap justify-content-end">
                <Dropdown
                  isOpen={excelDropdownOpen}
                  toggle={() => setExcelDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={!canExportAnyView || exportandoExcel}
                  >
                    {exportandoExcel ? (
                      <Spinner size="sm" className="me-1" />
                    ) : (
                      <i className="ri-file-excel-2-line me-1" />
                    )}
                    Excel
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem
                      onClick={() => handleExportExcel("activa")}
                      disabled={!canExportCurrentView}
                    >
                      <i className="ri-focus-3-line me-2" />
                      Pestaña activa ({TAB_CONFIG[activeTab].label})
                    </DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem
                      onClick={() => handleExportExcel("mes")}
                      disabled={!exportRowsByTab.mes}
                    >
                      <i className="ri-calendar-line me-2" />
                      Mensual
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => handleExportExcel("dia")}
                      disabled={!exportRowsByTab.dia}
                    >
                      <i className="ri-calendar-check-line me-2" />
                      Diario
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => handleExportExcel("semana")}
                      disabled={!exportRowsByTab.semana}
                    >
                      <i className="ri-calendar-2-line me-2" />
                      Semanal
                    </DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem
                      onClick={() => handleExportExcel("all")}
                      disabled={!canExportAnyView}
                    >
                      <i className="ri-file-copy-2-line me-2" />
                      Todas las pestañas
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Dropdown
                  isOpen={pdfDropdownOpen}
                  toggle={() => setPdfDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={!canExportAnyView || exportandoPdf}
                  >
                    {exportandoPdf ? (
                      <Spinner size="sm" className="me-1" />
                    ) : (
                      <i className="ri-file-pdf-2-line me-1" />
                    )}
                    PDF
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem
                      onClick={() => handleExportPdf("activa")}
                      disabled={!canExportCurrentView}
                    >
                      <i className="ri-focus-3-line me-2" />
                      Pestaña activa ({TAB_CONFIG[activeTab].label})
                    </DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem
                      onClick={() => handleExportPdf("mes")}
                      disabled={!exportRowsByTab.mes}
                    >
                      <i className="ri-calendar-line me-2" />
                      Mensual
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => handleExportPdf("dia")}
                      disabled={!exportRowsByTab.dia}
                    >
                      <i className="ri-calendar-check-line me-2" />
                      Diario
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => handleExportPdf("semana")}
                      disabled={!exportRowsByTab.semana}
                    >
                      <i className="ri-calendar-2-line me-2" />
                      Semanal
                    </DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem
                      onClick={() => handleExportPdf("all")}
                      disabled={!canExportAnyView}
                    >
                      <i className="ri-file-copy-2-line me-2" />
                      Todas las pestañas
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Button color="dark" className="btn-soft-dark" onClick={() => setFiltersOpen(true)}>
                  <i className="ri-equalizer-line me-1"></i>
                  Filtros
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <Nav tabs className="nav-tabs-custom mb-3">
              {(Object.keys(TAB_CONFIG) as TabId[]).map((tab) => (
                <NavItem key={tab}>
                  <NavLink
                    className={activeTab === tab ? "active" : ""}
                    onClick={() => handleTabChange(tab)}
                    style={{ cursor: "pointer" }}
                  >
                    <i className={`${TAB_CONFIG[tab].icon} me-1`} />
                    {TAB_CONFIG[tab].label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            {errorByTab[activeTab] ? (
              <Alert color="danger">{errorByTab[activeTab]}</Alert>
            ) : null}

            <div className="table-responsive">
              {activeTab === "mes" ? (
                <div>
                  <Nav pills className="mb-3 flex-nowrap overflow-auto gap-1 pb-1">
                    {MONTH_TAB_ITEMS.map((item) => {
                      const tieneDatos = mesesConDatos.has(item.value);
                      const estaActivo = mesSeleccionado === item.value;
                      return (
                        <NavItem key={item.value}>
                          <NavLink
                            href="#"
                            className={`${estaActivo ? "active" : ""} ${!tieneDatos ? "disabled text-muted" : ""}`}
                            style={{
                              cursor: tieneDatos ? "pointer" : "not-allowed",
                              opacity: tieneDatos ? 1 : 0.55,
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              if (!tieneDatos) return;
                              setMesSeleccionado(item.value);
                            }}
                          >
                            {item.label}
                          </NavLink>
                        </NavItem>
                      );
                    })}
                  </Nav>

                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                    <small className="text-muted">
                      {comparacionHastaFechaCargando
                        ? "Calculando corte con detalle diario..."
                        : puedeCompararHastaFecha
                          ? `Corte disponible: día ${String(diaCorteActual).padStart(2, "0")}`
                          : "Mes completo: no requiere corte"}
                      {ultimaSincronizacionLabel
                        ? ` · Últ. sincronización: ${ultimaSincronizacionLabel}`
                        : ""}
                    </small>
                    {badgeCorteTexto ? (
                      <Badge
                        color={
                          usarComparacionHastaFecha
                            ? "info"
                            : compararHastaFechaActual
                              ? "secondary"
                              : "light"
                        }
                        className={usarComparacionHastaFecha ? "text-dark" : ""}
                      >
                        {ultimaSincronizacionLabel
                          ? `${badgeCorteTexto} · Sync ${ultimaSincronizacionLabel}`
                          : badgeCorteTexto}
                      </Badge>
                    ) : null}
                  </div>

                  <Table hover className="align-middle mb-0 table-nowrap">
                    <thead className="table-light">
                      <tr className="align-middle">
                        <th
                          rowSpan={2}
                          onClick={() => cambiarOrdenMensual("sucursal")}
                          style={{ cursor: "pointer", verticalAlign: "middle" }}
                        >
                          Sucursal{iconoOrdenMensual("sucursal")}
                        </th>
                        {mesMostrarSubdivision ? (
                          <th rowSpan={2} style={SUBDIVISION_COLUMN_STYLE}>
                            Subdivisión
                          </th>
                        ) : null}
                        <th colSpan={4} className="text-center">
                          Ventas
                        </th>
                        <th colSpan={3} className="text-center">
                          Tickets
                        </th>
                      </tr>
                      <tr className="align-middle">
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenMensual("ventaAnterior")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Ventas año anterior (Gs){iconoOrdenMensual("ventaAnterior")}</span>
                            <small className="text-muted fw-normal">{rangoMensualAnterior}</small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenMensual("ventaActual")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Ventas año actual (Gs){iconoOrdenMensual("ventaActual")}</span>
                            <small
                              className={
                                resaltarFechaCorteActualEnTitulo
                                  ? "text-info fw-semibold"
                                  : "text-muted fw-normal"
                              }
                            >
                              {rangoMensualActual}
                            </small>
                            {ultimaSincronizacionLabel ? (
                              <small className="text-muted fw-normal">
                                Sync: {ultimaSincronizacionLabel}
                              </small>
                            ) : null}
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenMensual("variacionAbs")}
                          style={{ cursor: "pointer" }}
                        >
                          Diferencia (Gs){iconoOrdenMensual("variacionAbs")}
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenMensual("variacionPct")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>% Diferencia{iconoOrdenMensual("variacionPct")}</span>
                            <small className="text-muted fw-normal">de {rangoMensualAnterior}</small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenMensual("ticketsAnterior")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Tickets año anterior{iconoOrdenMensual("ticketsAnterior")}</span>
                            <small className="text-muted fw-normal">{rangoMensualAnterior}</small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenMensual("ticketsActual")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Tickets año actual{iconoOrdenMensual("ticketsActual")}</span>
                            <small
                              className={
                                resaltarFechaCorteActualEnTitulo
                                  ? "text-info fw-semibold"
                                  : "text-muted fw-normal"
                              }
                            >
                              {rangoMensualActual}
                            </small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenMensual("ticketsVariacionAbs")}
                          style={{ cursor: "pointer" }}
                        >
                          Dif. Tickets{iconoOrdenMensual("ticketsVariacionAbs")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {estaCargandoTabActivo ? (
                        <tr>
                          <td colSpan={mesColumnas} className="text-center py-4 text-muted">
                            <Spinner size="sm" className="me-2" />
                            {comparacionHastaFechaCargando
                              ? " Aplicando corte por última fecha del año actual..."
                              : " Cargando reporte..."}
                          </td>
                        </tr>
                      ) : mesRowsOrdenados.length === 0 ? (
                        <tr>
                          <td colSpan={mesColumnas} className="text-center py-4 text-muted">
                            Sin datos para el comparativo mensual.
                          </td>
                        </tr>
                      ) : mesFilasOrdenadas.length === 0 ? (
                        <tr>
                          <td colSpan={mesColumnas} className="text-center py-4 text-muted">
                            Sin datos para {MONTH_FULL_NAMES[mesSeleccionado - 1]}.
                          </td>
                        </tr>
                      ) : (
                        <>
                          {mesFilasOrdenadas.map((row) => (
                            <tr key={buildRowKey("mes", row)}>
                              <td>{row.sucursal}</td>
                              {mesMostrarSubdivision ? (
                                <td style={SUBDIVISION_COLUMN_STYLE}>
                                  {renderSubdivisionContent(row.subdivision)}
                                </td>
                              ) : null}
                              <td className="text-end">{formatCurrency(row.ventaAnterior)}</td>
                              <td className="text-end">{formatCurrency(row.ventaActual)}</td>
                              <td className={`text-end text-${variationColor(row.variacionAbs)}`}>
                                {formatCurrency(row.variacionAbs)}
                              </td>
                              <td className={`text-end text-${variationColor(row.variacionAbs)}`}>
                                {formatVariationPct(row.variacionPct)}
                              </td>
                              <td className="text-end">{formatNumber(row.ticketsAnterior)}</td>
                              <td className="text-end">{formatNumber(row.ticketsActual)}</td>
                              <td className={`text-end text-${variationColor(row.ticketsVariacionAbs)}`}>
                                {formatNumber(row.ticketsVariacionAbs)}
                              </td>
                            </tr>
                          ))}
                          <tr className="table-light fw-semibold">
                            <td>Total {MONTH_FULL_NAMES[mesSeleccionado - 1]}</td>
                            {mesMostrarSubdivision ? (
                              <td style={SUBDIVISION_COLUMN_STYLE}>-</td>
                            ) : null}
                            <td className="text-end">{formatCurrency(mesTotalesSeleccionados.ventaAnterior)}</td>
                            <td className="text-end">{formatCurrency(mesTotalesSeleccionados.ventaActual)}</td>
                            <td className="text-end">{formatCurrency(mesTotalesSeleccionados.variacionAbs)}</td>
                            <td
                              className={`text-end text-${variationColor(
                                mesTotalesSeleccionados.variacionAbs,
                              )}`}
                            >
                              {formatVariationPct(variacionPctTotalVentas)}
                            </td>
                            <td className="text-end">{formatNumber(mesTotalesSeleccionados.ticketsAnterior)}</td>
                            <td className="text-end">{formatNumber(mesTotalesSeleccionados.ticketsActual)}</td>
                            <td
                              className={`text-end text-${variationColor(
                                mesTotalesSeleccionados.ticketsVariacionAbs,
                              )}`}
                            >
                              {formatNumber(mesTotalesSeleccionados.ticketsVariacionAbs)}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </Table>
                </div>
              ) : activeTab === "dia" ? (
                <div>
                  <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
                    <div style={{ minWidth: 210 }}>
                      <small className="text-muted d-block mb-1">Mes</small>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={mesSeleccionado}
                        onChange={(event) => setMesSeleccionado(Number(event.target.value))}
                      >
                        {MONTH_TAB_ITEMS.map((item) => (
                          <option key={`dia-mes-${item.value}`} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Input>
                    </div>
                    <small className="text-muted">
                      Comparativo diario para {MONTH_FULL_NAMES[mesSeleccionado - 1]}
                    </small>
                  </div>

                  <Nav pills className="mb-3 flex-nowrap overflow-auto gap-1 pb-1">
                    {DAY_TAB_ITEMS.map((item) => {
                      const tieneDatos = diasConDatosMesSeleccionado.has(item.value);
                      const estaActivo = diaSeleccionado === item.value;
                      return (
                        <NavItem key={`dia-tab-${item.value}`}>
                          <NavLink
                            href="#"
                            className={`${estaActivo ? "active" : ""} ${!tieneDatos ? "disabled text-muted" : ""}`}
                            style={{
                              cursor: tieneDatos ? "pointer" : "not-allowed",
                              opacity: tieneDatos ? 1 : 0.55,
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              if (!tieneDatos) return;
                              setDiaSeleccionado(item.value);
                            }}
                          >
                            {item.label}
                          </NavLink>
                        </NavItem>
                      );
                    })}
                  </Nav>

                  <Table hover className="align-middle mb-0 table-nowrap">
                    <thead className="table-light">
                      <tr className="align-middle">
                        <th
                          rowSpan={2}
                          onClick={() => cambiarOrdenDiario("sucursal")}
                          style={{ cursor: "pointer", verticalAlign: "middle" }}
                        >
                          Sucursal{iconoOrdenDiario("sucursal")}
                        </th>
                        {diaMostrarSubdivision ? (
                          <th rowSpan={2} style={SUBDIVISION_COLUMN_STYLE}>
                            Subdivisión
                          </th>
                        ) : null}
                        <th colSpan={4} className="text-center">
                          Ventas
                        </th>
                        <th colSpan={3} className="text-center">
                          Tickets
                        </th>
                      </tr>
                      <tr className="align-middle">
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenDiario("ventaAnterior")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Ventas año anterior (Gs){iconoOrdenDiario("ventaAnterior")}</span>
                            <small className="text-muted fw-normal">{rangoDiarioAnterior}</small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenDiario("ventaActual")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Ventas año actual (Gs){iconoOrdenDiario("ventaActual")}</span>
                            <small
                              className={
                                resaltarFechaCorteDiarioEnTitulo
                                  ? "text-info fw-semibold"
                                  : "text-muted fw-normal"
                              }
                            >
                              {rangoDiarioActual}
                            </small>
                            {ultimaSincronizacionLabel ? (
                              <small className="text-muted fw-normal">
                                Sync: {ultimaSincronizacionLabel}
                              </small>
                            ) : null}
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenDiario("variacionAbs")}
                          style={{ cursor: "pointer" }}
                        >
                          Diferencia (Gs){iconoOrdenDiario("variacionAbs")}
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenDiario("variacionPct")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>% Diferencia{iconoOrdenDiario("variacionPct")}</span>
                            <small className="text-muted fw-normal">de {rangoDiarioAnterior}</small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenDiario("ticketsAnterior")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Tickets año anterior{iconoOrdenDiario("ticketsAnterior")}</span>
                            <small className="text-muted fw-normal">{rangoDiarioAnterior}</small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenDiario("ticketsActual")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Tickets año actual{iconoOrdenDiario("ticketsActual")}</span>
                            <small
                              className={
                                resaltarFechaCorteDiarioEnTitulo
                                  ? "text-info fw-semibold"
                                  : "text-muted fw-normal"
                              }
                            >
                              {rangoDiarioActual}
                            </small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenDiario("ticketsVariacionAbs")}
                          style={{ cursor: "pointer" }}
                        >
                          Dif. Tickets{iconoOrdenDiario("ticketsVariacionAbs")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingByTab.dia ? (
                        <tr>
                          <td colSpan={diaColumnas} className="text-center py-4 text-muted">
                            <Spinner size="sm" className="me-2" /> Cargando reporte diario...
                          </td>
                        </tr>
                      ) : diaRowsMesSeleccionado.length === 0 ? (
                        <tr>
                          <td colSpan={diaColumnas} className="text-center py-4 text-muted">
                            Sin datos diarios para {MONTH_FULL_NAMES[mesSeleccionado - 1]}.
                          </td>
                        </tr>
                      ) : diaFilasOrdenadas.length === 0 ? (
                        <tr>
                          <td colSpan={diaColumnas} className="text-center py-4 text-muted">
                            Sin datos para el día {String(diaSeleccionado).padStart(2, "0")} de{" "}
                            {MONTH_FULL_NAMES[mesSeleccionado - 1]}.
                          </td>
                        </tr>
                      ) : (
                        <>
                          {diaFilasOrdenadas.map((row) => (
                            <tr key={buildRowKey("dia", row)}>
                              <td>{row.sucursal}</td>
                              {diaMostrarSubdivision ? (
                                <td style={SUBDIVISION_COLUMN_STYLE}>
                                  {renderSubdivisionContent(row.subdivision)}
                                </td>
                              ) : null}
                              <td className="text-end">{formatCurrency(row.ventaAnterior)}</td>
                              <td className="text-end">{formatCurrency(row.ventaActual)}</td>
                              <td className={`text-end text-${variationColor(row.variacionAbs)}`}>
                                {formatCurrency(row.variacionAbs)}
                              </td>
                              <td className={`text-end text-${variationColor(row.variacionAbs)}`}>
                                {formatVariationPct(row.variacionPct)}
                              </td>
                              <td className="text-end">{formatNumber(row.ticketsAnterior)}</td>
                              <td className="text-end">{formatNumber(row.ticketsActual)}</td>
                              <td className={`text-end text-${variationColor(row.ticketsVariacionAbs)}`}>
                                {formatNumber(row.ticketsVariacionAbs)}
                              </td>
                            </tr>
                          ))}
                          <tr className="table-light fw-semibold">
                            <td>
                              Total día {String(diaSeleccionado).padStart(2, "0")} -{" "}
                              {MONTH_FULL_NAMES[mesSeleccionado - 1]}
                            </td>
                            {diaMostrarSubdivision ? (
                              <td style={SUBDIVISION_COLUMN_STYLE}>-</td>
                            ) : null}
                            <td className="text-end">{formatCurrency(diaTotalesSeleccionados.ventaAnterior)}</td>
                            <td className="text-end">{formatCurrency(diaTotalesSeleccionados.ventaActual)}</td>
                            <td className="text-end">{formatCurrency(diaTotalesSeleccionados.variacionAbs)}</td>
                            <td
                              className={`text-end text-${variationColor(
                                diaTotalesSeleccionados.variacionAbs,
                              )}`}
                            >
                              {formatVariationPct(variacionPctTotalVentasDia)}
                            </td>
                            <td className="text-end">{formatNumber(diaTotalesSeleccionados.ticketsAnterior)}</td>
                            <td className="text-end">{formatNumber(diaTotalesSeleccionados.ticketsActual)}</td>
                            <td
                              className={`text-end text-${variationColor(
                                diaTotalesSeleccionados.ticketsVariacionAbs,
                              )}`}
                            >
                              {formatNumber(diaTotalesSeleccionados.ticketsVariacionAbs)}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div>
                  <Nav pills className="mb-2 flex-nowrap overflow-auto gap-1 pb-1">
                    {SEMESTER_TAB_ITEMS.map((item) => {
                      const estaActivo = semestreSeleccionado === item.value;
                      const tieneDatosSemestre = semestresConDatos[item.value];
                      return (
                        <NavItem key={`semestre-tab-${item.value}`}>
                          <NavLink
                            href="#"
                            className={`${estaActivo ? "active" : ""} ${!tieneDatosSemestre ? "disabled text-muted" : ""}`}
                            style={{
                              cursor: tieneDatosSemestre ? "pointer" : "not-allowed",
                              opacity: tieneDatosSemestre ? 1 : 0.55,
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              if (!tieneDatosSemestre) return;
                              cambiarSemestreSemanal(item.value);
                            }}
                          >
                            {item.label}
                          </NavLink>
                        </NavItem>
                      );
                    })}
                  </Nav>

                  <Nav pills className="mb-3 flex-nowrap overflow-auto gap-1 pb-1">
                    {semanasDelSemestreSeleccionado.map((item) => {
                      const tieneDatos = semanasConDatos.has(item.value);
                      const estaActivo = semanaSeleccionada === item.value;
                      return (
                        <NavItem key={`semana-tab-${item.value}`}>
                          <NavLink
                            href="#"
                            className={`${estaActivo ? "active" : ""} ${!tieneDatos ? "disabled text-muted" : ""}`}
                            style={{
                              cursor: tieneDatos ? "pointer" : "not-allowed",
                              opacity: tieneDatos ? 1 : 0.55,
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              if (!tieneDatos) return;
                              seleccionarSemanaSemanal(item.value);
                            }}
                          >
                            {item.label}
                          </NavLink>
                        </NavItem>
                      );
                    })}
                  </Nav>

                  <Table hover className="align-middle mb-0 table-nowrap">
                    <thead className="table-light">
                      <tr className="align-middle">
                        <th
                          rowSpan={2}
                          onClick={() => cambiarOrdenSemanal("sucursal")}
                          style={{ cursor: "pointer", verticalAlign: "middle" }}
                        >
                          Sucursal{iconoOrdenSemanal("sucursal")}
                        </th>
                        {semanaMostrarSubdivision ? (
                          <th rowSpan={2} style={SUBDIVISION_COLUMN_STYLE}>
                            Subdivisión
                          </th>
                        ) : null}
                        <th colSpan={4} className="text-center">
                          Ventas
                        </th>
                        <th colSpan={3} className="text-center">
                          Tickets
                        </th>
                      </tr>
                      <tr className="align-middle">
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenSemanal("ventaAnterior")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Ventas año anterior (Gs){iconoOrdenSemanal("ventaAnterior")}</span>
                            <small className="text-muted fw-normal">{rangoSemanalAnterior}</small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenSemanal("ventaActual")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Ventas año actual (Gs){iconoOrdenSemanal("ventaActual")}</span>
                            <small
                              className={
                                resaltarFechaSemanalActualEnTitulo
                                  ? "text-info fw-semibold"
                                  : "text-muted fw-normal"
                              }
                            >
                              {rangoSemanalActual}
                            </small>
                            {ultimaSincronizacionLabel ? (
                              <small className="text-muted fw-normal">
                                Sync: {ultimaSincronizacionLabel}
                              </small>
                            ) : null}
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenSemanal("variacionAbs")}
                          style={{ cursor: "pointer" }}
                        >
                          Diferencia (Gs){iconoOrdenSemanal("variacionAbs")}
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenSemanal("variacionPct")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>% Diferencia{iconoOrdenSemanal("variacionPct")}</span>
                            <small className="text-muted fw-normal">de {rangoSemanalAnterior}</small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenSemanal("ticketsAnterior")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Tickets año anterior{iconoOrdenSemanal("ticketsAnterior")}</span>
                            <small className="text-muted fw-normal">{rangoSemanalAnterior}</small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenSemanal("ticketsActual")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex flex-column align-items-end">
                            <span>Tickets año actual{iconoOrdenSemanal("ticketsActual")}</span>
                            <small
                              className={
                                resaltarFechaSemanalActualEnTitulo
                                  ? "text-info fw-semibold"
                                  : "text-muted fw-normal"
                              }
                            >
                              {rangoSemanalActual}
                            </small>
                          </div>
                        </th>
                        <th
                          className="text-end"
                          onClick={() => cambiarOrdenSemanal("ticketsVariacionAbs")}
                          style={{ cursor: "pointer" }}
                        >
                          Dif. Tickets{iconoOrdenSemanal("ticketsVariacionAbs")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingByTab.semana ? (
                        <tr>
                          <td colSpan={semanaColumnas} className="text-center py-4 text-muted">
                            <Spinner size="sm" className="me-2" /> Cargando reporte semanal...
                          </td>
                        </tr>
                      ) : semanaRowsOrdenados.length === 0 ? (
                        <tr>
                          <td colSpan={semanaColumnas} className="text-center py-4 text-muted">
                            Sin datos para el comparativo semanal.
                          </td>
                        </tr>
                      ) : semanaFilasOrdenadas.length === 0 ? (
                        <tr>
                          <td colSpan={semanaColumnas} className="text-center py-4 text-muted">
                            Sin datos para la semana {String(semanaSeleccionada).padStart(2, "0")}.
                          </td>
                        </tr>
                      ) : (
                        <>
                          {semanaFilasOrdenadas.map((row) => (
                            <tr key={buildRowKey("semana", row)}>
                              <td>{row.sucursal}</td>
                              {semanaMostrarSubdivision ? (
                                <td style={SUBDIVISION_COLUMN_STYLE}>
                                  {renderSubdivisionContent(row.subdivision)}
                                </td>
                              ) : null}
                              <td className="text-end">{formatCurrency(row.ventaAnterior)}</td>
                              <td className="text-end">{formatCurrency(row.ventaActual)}</td>
                              <td className={`text-end text-${variationColor(row.variacionAbs)}`}>
                                {formatCurrency(row.variacionAbs)}
                              </td>
                              <td className={`text-end text-${variationColor(row.variacionAbs)}`}>
                                {formatVariationPct(row.variacionPct)}
                              </td>
                              <td className="text-end">{formatNumber(row.ticketsAnterior)}</td>
                              <td className="text-end">{formatNumber(row.ticketsActual)}</td>
                              <td className={`text-end text-${variationColor(row.ticketsVariacionAbs)}`}>
                                {formatNumber(row.ticketsVariacionAbs)}
                              </td>
                            </tr>
                          ))}
                          <tr className="table-light fw-semibold">
                            <td>Total Semana {String(semanaSeleccionada).padStart(2, "0")}</td>
                            {semanaMostrarSubdivision ? (
                              <td style={SUBDIVISION_COLUMN_STYLE}>-</td>
                            ) : null}
                            <td className="text-end">{formatCurrency(semanaTotalesSeleccionados.ventaAnterior)}</td>
                            <td className="text-end">{formatCurrency(semanaTotalesSeleccionados.ventaActual)}</td>
                            <td className="text-end">{formatCurrency(semanaTotalesSeleccionados.variacionAbs)}</td>
                            <td
                              className={`text-end text-${variationColor(
                                semanaTotalesSeleccionados.variacionAbs,
                              )}`}
                            >
                              {formatVariationPct(variacionPctTotalVentasSemana)}
                            </td>
                            <td className="text-end">{formatNumber(semanaTotalesSeleccionados.ticketsAnterior)}</td>
                            <td className="text-end">{formatNumber(semanaTotalesSeleccionados.ticketsActual)}</td>
                            <td
                              className={`text-end text-${variationColor(
                                semanaTotalesSeleccionados.ticketsVariacionAbs,
                              )}`}
                            >
                              {formatNumber(semanaTotalesSeleccionados.ticketsVariacionAbs)}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
        <Offcanvas
          direction="end"
          isOpen={filtersOpen}
          toggle={() => setFiltersOpen(false)}
          scrollable
        >
          <OffcanvasHeader toggle={() => setFiltersOpen(false)}>Filtros</OffcanvasHeader>
          <OffcanvasBody>
            <div className="d-flex flex-column gap-3">
              <div>
                <small className="text-muted d-block mb-1">Servidor</small>
                <Input
                  type="select"
                  bsSize="sm"
                  className="border border-warning"
                  style={{ boxShadow: "none", borderColor: "var(--bs-warning)" }}
                  value={serverSeleccionado}
                  onChange={(event) => handleServerChange(event.target.value)}
                  disabled={serverOptions.length === 0}
                >
                  {!serverSeleccionado ? (
                    <option value="" disabled>
                      Cargando servidores...
                    </option>
                  ) : null}
                  {serverOptions.map((server) => (
                    <option key={server} value={server}>
                      {server}
                    </option>
                  ))}
                </Input>
              </div>

              <div>
                <small className="text-muted d-block mb-1">Columnas</small>
                <div className="form-check form-switch m-0">
                  <Input
                    id="drawer-switch-subdivision"
                    type="switch"
                    role="switch"
                    checked={mostrarSubdivision}
                    onChange={(event) =>
                      setMostrarSubdivision(event.target.checked)
                    }
                  />
                  <label className="form-check-label" htmlFor="drawer-switch-subdivision">
                    Subdivisión
                  </label>
                </div>
                <div className="form-check form-switch m-0 mt-2">
                  <Input
                    id="drawer-switch-planta-produccion"
                    type="switch"
                    role="switch"
                    checked={mostrarPlantaProduccion}
                    onChange={(event) =>
                      setMostrarPlantaProduccion(event.target.checked)
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="drawer-switch-planta-produccion"
                  >
                    Sucursal Planta de Producción
                  </label>
                </div>
                {!subdivisionDisponibleEnTabActiva ? (
                  <small className="text-muted d-block mt-1">
                    Sin datos de subdivisión en esta vista.
                  </small>
                ) : null}
              </div>

              <div>
                <small className="text-muted d-block mb-1">Comparativo mensual</small>
                <div className="form-check form-switch m-0">
                  <Input
                    id="drawer-switch-corte-fecha"
                    type="switch"
                    role="switch"
                    checked={compararHastaFechaActual}
                    disabled={!puedeCompararHastaFecha && !compararHastaFechaActual}
                    onChange={(event) =>
                      setCompararHastaFechaActual(event.target.checked)
                    }
                  />
                  <label className="form-check-label" htmlFor="drawer-switch-corte-fecha">
                    Comparar ambos años hasta la última fecha con datos del año actual
                  </label>
                </div>
                {activeTab !== "mes" ? (
                  <small className="text-muted d-block mt-1">
                    Disponible en la pestaña Mensual.
                  </small>
                ) : null}
                <small className="text-muted d-block mt-2">
                  {comparacionHastaFechaCargando
                    ? "Calculando corte con detalle diario..."
                    : puedeCompararHastaFecha
                      ? `Corte disponible: día ${String(diaCorteActual).padStart(2, "0")}`
                      : "Mes completo: no requiere corte"}
                  {ultimaSincronizacionLabel
                    ? ` · Últ. sincronización: ${ultimaSincronizacionLabel}`
                    : ""}
                </small>
                {badgeCorteTexto ? (
                  <Badge
                    color={
                      usarComparacionHastaFecha
                        ? "info"
                        : compararHastaFechaActual
                          ? "secondary"
                          : "light"
                    }
                    className={`mt-2 ${usarComparacionHastaFecha ? "text-dark" : ""}`}
                  >
                    {ultimaSincronizacionLabel
                      ? `${badgeCorteTexto} · Sync ${ultimaSincronizacionLabel}`
                      : badgeCorteTexto}
                  </Badge>
                ) : null}
              </div>
            </div>
          </OffcanvasBody>
        </Offcanvas>
      </Container>
    </div>
  );
};

export default VentasComparativoOfflinePage;
